import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  REQUIREMENT_FACETS,
  REQUIREMENT_FACET_ORDER,
  type ElementCategory,
} from '@scoutaround/script-breakdown-shared';

import {
  SceneCard,
  type ElementDecision,
  type RequirementDecision,
  type SceneCardScene,
  type SceneElement,
  type TagScope,
  type TagTarget,
} from './SceneCard.js';
import { SceneRail, ValueLegend, railStateOf, type RailFilter, type RailScene, type RailSort } from './SceneRail.js';
import { useRefusal } from '../lib/permissions.js';
import { useModalFocus } from '../lib/useModalFocus.js';
import { SplitRequirementDialog } from './SplitRequirementDialog.js';
import { AddRequirementDialog, type SelectableScene } from './AddRequirementDialog.js';
import { Icon } from './Icon.js';
import { PageHeader } from './ui/PageHeader.js';
import { StatusPill, type WorkState } from './ui/StatusPill.js';
import { SplitWorkspace } from './ui/SplitWorkspace.js';
import { Disclosure } from './ui/Disclosure.js';
import { EmptyState } from './ui/EmptyState.js';
import { MoreMenu } from './ui/MoreMenu.js';
import '../styles/breakdown.css';

/**
 * Where the AI reading behind this page stands — the container's, from the
 * run's own record (`useScriptRun`, `extractionHasRun`). Never inferred from a
 * count: "no requirements" has two causes, and only the run says which.
 */
export type ReviewRunState = 'NOT_STARTED' | 'PROCESSING' | 'FAILED' | 'PARTIAL' | 'DONE';

/** The requirement figures, counted on the rows the server sent for this draft. */
export interface ReviewSummary {
  readonly awaiting: number;
  readonly approved: number;
  readonly notNeeded: number;
  readonly scenesWithoutRequirement: number;
}

export interface ReviewRequirementsData {
  readonly projectTitle: string;
  readonly scriptVersionLabel: string;
  readonly ready: boolean;
  readonly requirementCount: number;
  readonly shownRequirementCount: number;
  readonly railScenes: readonly RailScene[];
  readonly scenes: readonly SceneCardScene[];
  /** Absent: counted from the scenes' own requirement rows. */
  readonly summary?: ReviewSummary;
}

/**
 * Review requirements — the pass a producer makes over what the model read.
 *
 * ONE SCENE AT A TIME, BESIDE THE WHOLE SCREENPLAY. The navigator on the left
 * lists every scene in script order, filtered to the ones that need the
 * producer when they ask; the right shows the one being worked on — its
 * identity, its location requirements with what each must be like, and the
 * screenplay as a reference one click away. Stacking every scene made a
 * four-scene draft 2 200 pixels tall and a feature unreadable (redesign of
 * 2026-10-30). It is still not a list of requirements with the scenes hidden
 * behind them: the producer is checking a READING, and a reading is checked
 * scene by scene against the text it came from.
 *
 * THE HEADER SAYS WHERE THE READING STANDS AND OFFERS ONE THING TO DO: run the
 * breakdown, wait for it, review what awaits a decision, or go on to the
 * search. Everything else is a neutral control.
 *
 * THE SCRIPT IS READ-ONLY HERE, and says so. Everything on this screen edits the
 * BREAKDOWN — what the model understood — and none of it edits the screenplay,
 * which belongs to the production and arrives as a file.
 *
 * MERGE AND SPLIT ARE FIRST-CLASS, not tidying. Two requirements that are one
 * place, or one that is really two, are the model's most consequential mistakes:
 * they change how many locations get searched for, scouted and booked. The
 * selection that feeds them survives moving between scenes, because the two
 * requirements that are really one place are usually not in the same scene.
 */
export function ReviewRequirementsScreen({
  data,
  busy = false,
  error = null,
  onTag,
  onAddRequirement,
  onMarkSceneReviewed,
  onUnmarkSceneReviewed,
  onAddElement,
  onDecideElement,
  onRemoveElement,
  onSetQuantity,
  onContinueToMatching,
  continueBlockedReason = null,
  continueNote = null,
  onDecideRequirement,
  onRestoreRequirement,
  onMerge,
  onSplit,
  settled = null,
  onUndo,
  analyse = null,
  pickedSceneIds,
  onPickScenes,
  bulkActions = null,
  projectId,
  focus = 'ALL',
  continueLabel = 'Explore matching locations',
  runState = 'DONE',
  runNote = null,
  initialSceneId = null,
  onSceneChange,
}: {
  data: ReviewRequirementsData;
  /**
   * WHICH WORKFLOW THIS IS (owner brief, 2026-10-29). `LOCATIONS` — the
   * requirements, their decisions, merge and split; `ELEMENTS` — the detailed
   * breakdown, scene by scene, and its review gate; `ALL` — the Script tab,
   * which keeps both on one card.
   */
  focus?: 'ALL' | 'LOCATIONS' | 'ELEMENTS';
  /** The primary action's words; the Locations default goes on to matching. */
  continueLabel?: string;
  /** Puts "Discuss" controls on each scene, requirement and confirmed element (ADR 0021). */
  projectId?: string;
  busy?: boolean;
  error?: string | null;
  onTag?: (sceneId: string, selection: string, target: TagTarget, scope: TagScope) => void;
  onAddRequirement?: (input: { title: string; sceneIds: readonly string[] }) => void;
  onMarkSceneReviewed?: (sceneId: string) => void;
  onUnmarkSceneReviewed?: (sceneId: string) => void;
  onAddElement?: (sceneId: string, category: ElementCategory, name: string) => void;
  onDecideElement?: (mentionId: string, decision: ElementDecision) => void;
  onRemoveElement?: (sceneId: string, productionElementId: string) => void;
  onSetQuantity?: (sceneId: string, element: SceneElement, quantity: number | null) => void;
  onContinueToMatching?: () => void;
  /**
   * Why Continue is not available yet — "2 requirements still need a
   * decision". Shown beside the disabled button, because a grey button with
   * no reason reads as a broken one.
   */
  continueBlockedReason?: string | null;
  /**
   * What is still open while Continue IS available — "3 requirements still
   * need a decision · they will not be searched for". Since the owner's review
   * of 2026-10-27 the breakdown no longer has to be complete to go on (D1), so
   * what used to be the blocked reason is said beside an enabled button, and
   * the container asks before it moves.
   */
  continueNote?: string | null;
  /**
   * THE WAY OUT OF AN EMPTY BREAKDOWN, shown only when there is nothing here.
   *
   * A node rather than a callback: this screen is presentational, and the
   * control needs the project's mutation, its refusals and the run poll. The
   * container passes `AnalyseWithAi` and decides WHEN — at zero requirements,
   * and when a run reported scenes it could not carry. Until 2026-10-26 this
   * page could say "We identified 0 location requirements across 120 scenes"
   * with no control anywhere on it that could change that — the only trigger
   * lived in a tab the guided flow never names.
   */
  analyse?: ReactNode;
  /** Scenes picked on the rail for a bulk action; absent, the rail has no boxes. */
  pickedSceneIds?: ReadonlySet<string>;
  onPickScenes?: (sceneIds: readonly string[], on: boolean) => void;
  /**
   * What to do with the picked scenes — the container's, because it holds the
   * requirement rows and the verbs; this screen only gives it a place.
   */
  bulkActions?: ReactNode;
  onDecideRequirement?: (requirementId: string, decision: RequirementDecision) => void;
  /** Undo "We don't need this" from the scene card. */
  onRestoreRequirement?: (requirementId: string) => void;
  onMerge?: (requirementIds: readonly string[]) => void;
  /**
   * Bumped by the container when a merge or split has actually landed — the
   * signal that closes the dialog and clears the selection. A counter rather
   * than a boolean so two merges in a row are two distinct events.
   */
  settled?: number | null;
  onSplit?: (
    requirementId: string,
    input: { title: string; sceneIds: readonly string[] },
  ) => void;
  onUndo?: () => void;
  /** Where the AI reading stands; absent is a finished reading. */
  runState?: ReviewRunState;
  /** The failure or the partial reading, in words, when there is one. */
  runNote?: string | null;
  /** The scene to open on (an address, `?scene=`); absent, the first that needs the producer. */
  initialSceneId?: string | null;
  /** Told every time the scene on the right changes, so a route can keep it in the address. */
  onSceneChange?: (sceneId: string) => void;
}) {
  const [sort, setSort] = useState<RailSort>('SCRIPT_ORDER');
  const [filter, setFilter] = useState<RailFilter>('ALL');
  const [highlights, setHighlights] = useState(true);
  /**
   * THE SCENE ON THE RIGHT. Chosen once — the address's scene, else the first
   * that needs the producer — and then only by the producer: a decision that
   * clears a scene must not make the page jump to another one under them.
   */
  const [focused, setFocused] = useState<string | null>(initialSceneId);
  /** On a phone the list and the scene take turns; this says which is showing. */
  const [showDetail, setShowDetail] = useState(initialSceneId !== null);
  const [adding, setAdding] = useState<{ sceneId: string | null } | null>(null);
  /**
   * WHAT MERGE AND SPLIT ACT ON. Held here, not on a card, because a merge is
   * precisely the operation that spans scenes — so the ticks survive moving
   * from one scene to the next.
   */
  const [selectedRequirements, setSelectedRequirements] = useState<readonly string[]>([]);
  /**
   * WHY A CONTROL IS GREY, IN WORDS (ADR 0014's UX ruling). The hint is never
   * the gate — `requirePermission` on the server is — and an unknown answer is
   * YES, because hiding a control somebody is entitled to leaves them no way to
   * find out why.
   */
  const decideRefusal = useRefusal('REQUIREMENT_APPROVE');
  const reviewRefusal = useRefusal('SCRIPT_REVIEW');

  const [confirmingMerge, setConfirmingMerge] = useState(false);
  const [splitting, setSplitting] = useState<string | null>(null);

  /**
   * THE DIALOG CLOSES WHEN THE SERVER SAYS SO, NOT WHEN THE BUTTON IS PRESSED.
   * A merge the server refused used to take the dialog away AND wipe the ticks;
   * the container flips `settled` once the mutation resolves.
   */
  useEffect(() => {
    if (settled === null) return;

    setConfirmingMerge(false);
    setSplitting(null);
    setSelectedRequirements([]);
  }, [settled]);

  const toggleRequirement = useCallback((requirementId: string) => {
    setSelectedRequirements((current) =>
      current.includes(requirementId)
        ? current.filter((id) => id !== requirementId)
        : [...current, requirementId],
    );
  }, []);

  /** PRESELECTED: adding from a scene starts with that scene ticked. */
  const openAddRequirement = useCallback((sceneId: string) => {
    setAdding({ sceneId });
  }, []);

  const stateById = useMemo(
    () => new Map(data.railScenes.map((s) => [s.id, railStateOf(s)])),
    [data.railScenes],
  );
  const attentionIds = data.scenes.filter((s) => stateById.get(s.id) === 'ATTENTION').map((s) => s.id);
  /**
   * The scenes with a location still awaiting a decision — what "Review
   * flagged requirements" walks first. A scene with no location at all also
   * needs attention, but there is nothing on it to decide, so it comes after.
   */
  const flaggedIds =
    focus === 'ELEMENTS'
      ? attentionIds
      : data.scenes
          .filter((s) => (s.requirements ?? []).some((r) => r.status === 'PROPOSED' || r.status === 'DRAFT'))
          .map((s) => s.id);

  // The first scene to show: the address's, else the first that needs a decision.
  const defaultSceneId = flaggedIds[0] ?? attentionIds[0] ?? data.scenes[0]?.id ?? null;
  const currentId =
    focused !== null && data.scenes.some((s) => s.id === focused) ? focused : defaultSceneId;
  const current = currentId === null ? null : (data.scenes.find((s) => s.id === currentId) ?? null);

  useEffect(() => {
    if (focused === null && defaultSceneId !== null) setFocused(defaultSceneId);
  }, [focused, defaultSceneId]);

  const select = useCallback(
    (sceneId: string) => {
      setFocused(sceneId);
      setShowDetail(true);
      onSceneChange?.(sceneId);
    },
    [onSceneChange],
  );

  /** The scenes Previous and Next walk: the filtered list, in script order. */
  const walk = data.scenes
    .filter((s) => filter === 'ALL' || stateById.get(s.id) === filter)
    .map((s) => s.id);
  const order = data.scenes.map((s) => s.id);
  const at = currentId === null ? -1 : order.indexOf(currentId);
  const nextId = order.slice(at + 1).find((id) => walk.includes(id)) ?? null;
  const prevId = [...order.slice(0, Math.max(0, at))].reverse().find((id) => walk.includes(id)) ?? null;

  // Changing the filter keeps the scene when it is still in the list, and
  // otherwise moves to the first scene the list now shows.
  const changeFilter = (next: RailFilter) => {
    setFilter(next);
    if (currentId === null || next === 'ALL' || stateById.get(currentId) === next) return;
    const first = data.scenes.find((s) => stateById.get(s.id) === next);
    if (first !== undefined) select(first.id);
  };

  /**
   * "Review flagged requirements": the list narrowed to the scenes that need
   * the producer, opened on the next one with something to decide.
   */
  const reviewFlagged = () => {
    setFilter('ATTENTION');
    const targets = flaggedIds.length > 0 ? flaggedIds : attentionIds;
    const after = order.slice(at + 1).find((id) => targets.includes(id));
    const target = after ?? targets[0];
    if (target !== undefined) select(target);
  };

  /**
   * The scene as drawn — stripped of highlights when they are off. Highlights
   * off shows the screenplay as written, without the model's opinion painted
   * over it.
   */
  const painted = useMemo(
    () => (current === null ? null : highlights ? current : stripHighlights(current)),
    [current, highlights],
  );

  const selectable: readonly SelectableScene[] = useMemo(
    () =>
      data.scenes.map((scene) => ({
        id: scene.id,
        sceneNumber: scene.sceneNumber,
        slugline: scene.slugline,
      })),
    [data.scenes],
  );

  /**
   * The selected requirements as rows, gathered across every scene and
   * deduplicated by id: a requirement shared by four scenes is one location,
   * and two ticks on it must not look like a mergeable pair.
   */
  const selectedRows = useMemo(() => {
    const byId = new Map<string, { id: string; title: string; scenes: readonly SelectableScene[] }>();

    for (const scene of data.scenes) {
      for (const r of scene.requirements ?? []) {
        if (!selectedRequirements.includes(r.id)) continue;
        const existing = byId.get(r.id);
        const entry = {
          id: r.id,
          title: r.title,
          scenes: [
            ...(existing?.scenes ?? []),
            { id: scene.id, sceneNumber: scene.sceneNumber, slugline: scene.slugline },
          ],
        };
        byId.set(r.id, entry);
      }
    }

    return selectedRequirements.flatMap((id) => {
      const row = byId.get(id);

      return row === undefined ? [] : [row];
    });
  }, [data.scenes, selectedRequirements]);

  const canMerge = selectedRequirements.length >= 2;
  /** The detailed breakdown's progress: scenes whose sheet a person marked reviewed. */
  const reviewedScenes = data.scenes.filter((scene) => scene.reviewStatus === 'REVIEWED').length;
  const changedScenes = data.scenes.filter((scene) => scene.reviewStatus === 'IN_REVIEW').length;
  /**
   * A requirement on one scene has nothing to split off. The server refuses it,
   * and a button that opens a dialog whose only outcome is that refusal is
   * worse than a disabled one that says why.
   */
  const splitCandidate = selectedRows.length === 1 ? selectedRows[0] : undefined;
  const canSplit = splitCandidate !== undefined && splitCandidate.scenes.length >= 2;

  const summary = data.summary ?? summaryOf(data.scenes);
  const elements = focus === 'ELEMENTS';
  const requirementWord = (n: number) => `location requirement${n === 1 ? '' : 's'}`;

  // ---- the header: where the reading stands, and the one thing to do next ----

  const headline = elements
    ? `${String(reviewedScenes)} of ${String(data.scenes.length)} scene${data.scenes.length === 1 ? '' : 's'} broken down`
    : runState === 'PROCESSING' && data.requirementCount === 0
      ? 'Reading the screenplay'
      : data.requirementCount === 0
        ? runState === 'NOT_STARTED'
          ? 'No location requirements yet'
          : 'No location requirements identified'
        : `${String(data.requirementCount)} ${requirementWord(data.requirementCount)} identified`;

  const state: { state: WorkState; label?: string } =
    runState === 'PROCESSING'
      ? { state: 'PROCESSING' }
      : runState === 'FAILED'
        ? { state: 'FAILED', label: 'Breakdown failed' }
        : runState === 'PARTIAL'
          ? { state: 'NEEDS_ATTENTION', label: 'Partly read' }
          : elements
            ? data.scenes.length > 0 && reviewedScenes === data.scenes.length
              ? { state: 'COMPLETED', label: 'Reviewed' }
              : reviewedScenes > 0
                ? { state: 'IN_PROGRESS' }
                : runState === 'NOT_STARTED'
                  ? { state: 'NOT_STARTED' }
                  : { state: 'READY_FOR_REVIEW' }
            : data.requirementCount === 0
              ? runState === 'NOT_STARTED'
                ? { state: 'NOT_STARTED' }
                : { state: 'NEEDS_ATTENTION', label: 'Nothing to search for' }
              : summary.awaiting > 0
                ? { state: 'READY_FOR_REVIEW' }
                : { state: 'COMPLETED', label: 'Reviewed' };

  const lede =
    runState === 'PROCESSING'
      ? 'The AI breakdown is running. Everything it finds arrives together when it finishes — you can leave this page.'
      : (runState === 'FAILED' || runState === 'PARTIAL') && runNote !== null
        ? runNote
        : elements
          ? 'Go scene by scene: tag what each scene needs by selecting words in its text, then mark the scene reviewed.'
          : data.requirementCount === 0
            ? runState === 'NOT_STARTED'
              ? 'Run the AI breakdown to work out the locations the production needs, or add a requirement by hand.'
              : 'The AI breakdown found no location in the scenes. Add a requirement by hand for any place the production needs.'
            : summary.awaiting > 0
              ? 'Review the requirements before searching for matching filming locations.'
              : 'Every requirement has a decision. The approved ones can be searched for.';

  const unreviewed = data.scenes.length - reviewedScenes;
  // ONE red button. The paid reading, while it is on offer, is the main task;
  // after it, reviewing what awaits a decision; after that, going on.
  const offersAnalysis = analyse !== null && runState !== 'PROCESSING';
  const nextAction =
    current === null
      ? null
      : elements
        ? unreviewed > 0
          ? { label: 'Review next scene', run: reviewFlagged }
          : null
        : summary.awaiting > 0
          ? { label: 'Review flagged requirements', run: reviewFlagged }
          : null;
  const continuePrimary = !offersAnalysis && nextAction === null;

  const metrics: readonly { id: string; label: string; value: string; tone?: 'attention' }[] = elements
    ? [
        { id: 'reviewed', label: 'Scenes reviewed', value: `${String(reviewedScenes)} of ${String(data.scenes.length)}` },
        ...(changedScenes > 0
          ? [{ id: 'changed', label: 'Changed since reviewed', value: String(changedScenes), tone: 'attention' as const }]
          : []),
      ]
    : data.requirementCount === 0 && runState !== 'DONE'
      ? []
      : [
          { id: 'requirements', label: 'Requirements', value: String(data.requirementCount) },
          {
            id: 'awaiting',
            label: 'Awaiting your decision',
            value: String(summary.awaiting),
            tone: summary.awaiting > 0 ? ('attention' as const) : undefined,
          },
          { id: 'approved', label: 'Approved', value: String(summary.approved) },
          ...(summary.notNeeded > 0 ? [{ id: 'not-needed', label: 'Not needed', value: String(summary.notNeeded) }] : []),
          {
            id: 'without',
            label: 'Scenes without a requirement',
            value: String(summary.scenesWithoutRequirement),
          },
        ];

  const workflow = elements ? 'Detailed breakdown' : focus === 'LOCATIONS' ? 'Locations' : 'Script analysis';

  return (
    <section data-testid="review-requirements" className="rq">
      <div className="rq-head">
        <PageHeader
          testId="review-page-head"
          eyebrow={`${workflow} · ${data.scriptVersionLabel}`}
          title={<span data-testid="review-headline">{headline}</span>}
          status={<StatusPill state={state.state} label={state.label} testId="review-state" />}
          lede={<span data-testid="review-intro">{lede}</span>}
          actions={
            <>
              {nextAction === null ? null : (
                <button
                  type="button"
                  className={offersAnalysis ? 'btn btn-ghost' : 'btn btn-primary'}
                  data-testid="review-next-action"
                  disabled={busy}
                  onClick={nextAction.run}
                >
                  {nextAction.label}
                  <Icon name="arrowRight" size={16} />
                </button>
              )}
              {onContinueToMatching === undefined ? null : (
                <button
                  type="button"
                  className={continuePrimary ? 'btn btn-primary' : 'btn btn-ghost'}
                  data-testid="continue-to-matching"
                  disabled={busy || continueBlockedReason !== null}
                  onClick={() => onContinueToMatching()}
                >
                  {continueLabel}
                  {continuePrimary ? <Icon name="arrowRight" size={16} /> : null}
                </button>
              )}
            </>
          }
        />

        {continueBlockedReason === null && continueNote === null ? null : (
          <p className="rq-head-note">
            {continueBlockedReason === null ? null : (
              <span className="mm" data-testid="continue-blocked">
                {continueBlockedReason}
              </span>
            )}
            {continueBlockedReason !== null || continueNote === null ? null : (
              <span className="rq-head-warn" data-testid="continue-note">
                {continueNote}
              </span>
            )}
          </p>
        )}

        {metrics.length === 0 ? null : (
          <dl className="rq-metrics" data-testid="review-metrics">
            {metrics.map((m) => (
              <div key={m.id} data-tone={m.tone} data-testid={`review-metric-${m.id}`}>
                <dt>{m.label}</dt>
                <dd>{m.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {/* WHETHER TO OFFER A READING IS THE CONTAINER'S CALL: at zero
            requirements, after a failed run, and when a run reported scenes it
            could not carry. While a run is going this slot carries the
            progress instead, never a second paid request. */}
        {analyse !== null ? (
          <div className="rq-analyse" data-testid="review-empty-analyse">
            {analyse}
          </div>
        ) : null}
      </div>

      {bulkActions}

      {data.scenes.length === 0 ? (
        <EmptyState title="No scenes in this draft" testId="review-no-scenes">
          The breakdown works scene by scene. Once the screenplay&rsquo;s scenes are confirmed they appear here.
        </EmptyState>
      ) : (
        <SplitWorkspace
          testId="review-workspace"
          navLabel="Scenes"
          backLabel="All scenes"
          showDetail={showDetail}
          onBackToList={() => {
            setShowDetail(false);
          }}
          nav={
            <SceneRail
              scenes={data.railScenes}
              sort={sort}
              focusedSceneId={currentId}
              onSort={setSort}
              onFocus={select}
              picked={pickedSceneIds}
              onPick={onPickScenes}
              filter={filter}
              onFilter={changeFilter}
              attentionLabel={elements ? 'Not reviewed' : 'Needs attention'}
              attentionState={elements ? 'READY_FOR_REVIEW' : 'NEEDS_ATTENTION'}
            />
          }
          detail={
            <div className="rq-detail" data-testid="review-detail">
              <div className="ui-toolbar rq-detail-bar">
                <span className="rq-position" data-testid="review-scene-position" aria-live="polite">
                  {current === null
                    ? 'No scene selected'
                    : `Scene ${current.sceneNumber ?? String(current.ordinal ?? at + 1)} · ${String(at + 1)} of ${String(data.scenes.length)}`}
                  {filter === 'ALL' ? null : (
                    <span className="mm">{` · ${String(walk.length)} ${filter === 'ATTENTION' ? (elements ? 'not reviewed' : 'need attention') : 'reviewed'}`}</span>
                  )}
                </span>
                <span className="ui-toolbar-end">
                  {focus === 'LOCATIONS' ? null : (
                    <button
                      type="button"
                      className="ctl"
                      data-testid="toggle-highlights"
                      aria-pressed={highlights}
                      onClick={() => setHighlights((on) => !on)}
                    >
                      {highlights ? 'Highlights on' : 'Highlights off'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="ctl rq-step"
                    data-testid="review-scene-prev"
                    disabled={prevId === null}
                    onClick={() => {
                      if (prevId !== null) select(prevId);
                    }}
                  >
                    <Icon name="arrowLeft" size={14} />
                    Previous
                  </button>
                  <button
                    type="button"
                    className="ctl rq-step"
                    data-testid="review-scene-next"
                    disabled={nextId === null}
                    onClick={() => {
                      if (nextId !== null) select(nextId);
                    }}
                  >
                    Next
                    <Icon name="arrowRight" size={14} />
                  </button>
                </span>
              </div>

              {elements ? null : (
                <div className="rq-corrections">
                  <div className="ui-toolbar">
                    <span className="mm" data-testid="requirement-count">
                      {`${String(data.requirementCount)} ${requirementWord(data.requirementCount)} in this draft`}
                    </span>
                    <span className="ui-toolbar-end">
                      <button
                        type="button"
                        className="ctl"
                        data-testid="merge-requirements"
                        disabled={!canMerge || busy || decideRefusal !== null}
                        title={decideRefusal ?? undefined}
                        onClick={() => setConfirmingMerge(true)}
                      >
                        Merge
                      </button>
                      <button
                        type="button"
                        className="ctl"
                        data-testid="split-requirement"
                        disabled={!canSplit || busy || decideRefusal !== null}
                        title={decideRefusal ?? undefined}
                        onClick={() => {
                          if (splitCandidate !== undefined) setSplitting(splitCandidate.id);
                        }}
                      >
                        Split
                      </button>
                      <button
                        type="button"
                        className="ctl"
                        data-testid="open-add-requirement"
                        disabled={busy || decideRefusal !== null}
                        title={decideRefusal ?? undefined}
                        onClick={() => setAdding({ sceneId: currentId })}
                      >
                        Add requirement
                      </button>
                      {/* Undo stays named, and stays disabled, until there is a
                          verb behind it: a merge moved scene links and
                          superseded rows that may have been edited since, so
                          "put it back" is a design question rather than a
                          missing endpoint. */}
                      <MoreMenu
                        testId="review-more"
                        label="More requirement actions"
                        items={[
                          {
                            id: 'undo',
                            label: 'Undo the last merge or split',
                            disabled: true,
                            hint: 'Not available yet — a merge or split is not reversed by a simple inverse.',
                            testId: 'undo-requirement-change',
                            onSelect: () => onUndo?.(),
                          },
                        ]}
                      />
                    </span>
                  </div>

                  {/* The selection state is spelled out rather than left to the
                      button styling: "why is Merge grey?" is otherwise a dead end.
                      Announced, because the tick that changed it may be on a
                      scene that is no longer on screen. */}
                  <p
                    data-testid="selection-status"
                    className="rq-selection"
                    data-active={selectedRequirements.length > 0 ? '1' : '0'}
                    role="status"
                    aria-live="polite"
                  >
                    {selectedRequirements.length === 0 ? (
                      'Nothing selected — tick one requirement to split it, or two or more to merge them. Ticks stay as you move between scenes.'
                    ) : (
                      <>
                        {`${String(selectedRequirements.length)} selected: ${selectedRows.map((r) => r.title).join(', ')}`}
                        {selectedRows.length === 1 && !canSplit
                          ? ' — on one scene only, so there is nothing to split off.'
                          : ''}{' '}
                        <button
                          type="button"
                          className="ctl"
                          data-testid="clear-requirement-selection"
                          onClick={() => setSelectedRequirements([])}
                        >
                          Clear
                        </button>
                      </>
                    )}
                  </p>
                </div>
              )}

              {error === null ? null : (
                <p data-testid="review-error" className="rq-error" role="alert">
                  {error}
                </p>
              )}

              {/* EVERY HANDLER IS HOISTED, none built here: the card is
                  memoised, and a closure created in render is a new reference
                  each time that would defeat it. */}
              {painted === null ? null : (
                <SceneCard
                  key={painted.id}
                  scene={painted}
                  projectId={projectId}
                  onTag={onTag}
                  onAddRequirement={openAddRequirement}
                  onMarkReviewed={onMarkSceneReviewed}
                  onUnmarkReviewed={onUnmarkSceneReviewed}
                  onAddElement={onAddElement}
                  onDecideElement={onDecideElement}
                  onRemoveElement={onRemoveElement}
                  onSetQuantity={onSetQuantity}
                  onDecideRequirement={onDecideRequirement}
                  onRestoreRequirement={onRestoreRequirement}
                  selectedRequirementIds={selectedRequirements}
                  onToggleRequirementSelected={toggleRequirement}
                  decideRefusal={decideRefusal}
                  reviewRefusal={reviewRefusal}
                  focus={focus}
                />
              )}

              {elements ? null : (
                <Disclosure summary="How the values are used" testId="review-legend">
                  <ValueLegend />
                  <ul className="rq-facets" aria-label="What each kind of value answers">
                    {REQUIREMENT_FACET_ORDER.map((facet) => (
                      <li key={facet}>
                        <i aria-hidden="true" style={{ background: REQUIREMENT_FACETS[facet].colour }} />
                        <b>{REQUIREMENT_FACETS[facet].label}</b> — {REQUIREMENT_FACETS[facet].use}
                      </li>
                    ))}
                  </ul>
                </Disclosure>
              )}
            </div>
          }
        />
      )}

      {adding === null ? null : (
        <AddRequirementDialog
          scenes={selectable}
          preselectedSceneIds={adding.sceneId === null ? [] : [adding.sceneId]}
          busy={busy}
          error={error}
          onCancel={() => setAdding(null)}
          onSubmit={(input) => {
            onAddRequirement?.(input);
            setAdding(null);
          }}
        />
      )}

      {!confirmingMerge || selectedRows.length < 2 ? null : (
        <MergeConfirmDialog
          requirements={selectedRows}
          busy={busy}
          error={error}
          onCancel={() => setConfirmingMerge(false)}
          onConfirm={() => onMerge?.(selectedRequirements)}
        />
      )}

      {splitting === null || splitCandidate === undefined ? null : (
        <SplitRequirementDialog
          requirementTitle={splitCandidate.title}
          scenes={splitCandidate.scenes}
          busy={busy}
          error={error}
          onCancel={() => setSplitting(null)}
          onSubmit={(input) => onSplit?.(splitCandidate.id, input)}
        />
      )}
    </section>
  );
}

/**
 * The figures, counted on the scenes' own requirement rows — each requirement
 * once however many scenes carry it. The container passes the server rows'
 * figures instead when it holds them.
 */
function summaryOf(scenes: readonly SceneCardScene[]): ReviewSummary {
  const status = new Map<string, string>();
  for (const scene of scenes) for (const r of scene.requirements ?? []) status.set(r.id, r.status);
  const all = [...status.values()];

  return {
    awaiting: all.filter((s) => s === 'PROPOSED' || s === 'DRAFT').length,
    approved: all.filter((s) => s === 'APPROVED').length,
    notNeeded: all.filter((s) => s === 'DISMISSED').length,
    scenesWithoutRequirement: scenes.filter((s) => (s.requirements ?? []).length === 0).length,
  };
}

/**
 * Highlights off shows the screenplay as written.
 *
 * Not decoration: after a while the colours become the thing you read, and a
 * producer checking whether a scene was understood correctly needs to be able
 * to see the text WITHOUT the model's opinion painted over it.
 */
function stripHighlights(scene: SceneCardScene): SceneCardScene {
  return {
    ...scene,
    lines: scene.lines.map((line) => ({
      ...line,
      spans: line.spans.map(({ text }) => ({ text })),
    })),
  };
}


/**
 * MERGING ASKS FIRST, because nothing on this screen takes it back.
 *
 * Undo is deliberately disabled here — a merge moves scene links and supersedes
 * rows that may have been edited since, so its inverse is a design question
 * rather than a missing endpoint. That makes the moment before the press the
 * only place to be sure, so the outcome is spelled out: which locations are
 * involved, what survives, and that it cannot be walked back.
 *
 * ITS OWN COMPONENT so the focus trap mounts with the dialog. Called from the
 * screen body it would have run once, when the screen appeared, and moved focus
 * into a dialog nobody had opened yet.
 */
function MergeConfirmDialog({
  requirements,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  requirements: readonly { id: string; title: string }[];
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const modal = useModalFocus<HTMLDivElement>(() => {
    if (!busy) onCancel();
  });

  return (
    <div
      data-testid="merge-requirements-confirm"
      onKeyDown={modal.onKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="merge-requirements-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(35, 35, 35, 0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px',
        overflowY: 'auto',
        zIndex: 40,
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div
        ref={modal.ref}
        style={{
          background: 'var(--color-bg)',
          width: 'min(560px, 100%)',
          border: '1px solid var(--color-divider)',
          boxShadow: '0 18px 48px rgba(16, 24, 40, 0.18)',
        }}
      >
        <header style={{ padding: '18px 22px', borderBottom: '2px solid var(--ink)' }}>
          <h2
            id="merge-requirements-title"
            style={{
              margin: 0,
              fontFamily: 'var(--font-heading)',
              fontSize: 22,
              letterSpacing: '0.02em',
            }}
          >
            MERGE REQUIREMENTS
          </h2>
        </header>

        <div style={{ padding: '18px 22px', fontSize: 13, lineHeight: 1.6 }}>
          <p style={{ margin: 0 }}>
            {`${String(requirements.length)} requirements become one place:`}
          </p>
          <ul style={{ margin: '10px 0 0', paddingLeft: 20 }}>
            {requirements.map((r) => (
              <li key={r.id} data-testid={`merge-member-${r.id}`}>
                {r.title}
              </li>
            ))}
          </ul>
          <p className="mm" style={{ marginTop: 14, marginBottom: 0 }}>
            The oldest of them survives and takes on every scene the others cover. The others are
            marked superseded — kept, never deleted, so the history stays readable — and this screen
            cannot undo it.
          </p>

          {error === null ? null : (
            <p data-testid="merge-requirements-error" style={{ marginTop: 12, color: 'var(--danger)' }}>
              {error}
            </p>
          )}
        </div>

        <footer
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            padding: '14px 22px',
            borderTop: '1px solid var(--color-divider)',
            background: 'var(--color-surface)',
          }}
        >
          <button
            type="button"
            className="btn btn-ghost"
            data-testid="merge-requirements-cancel"
            disabled={busy}
            onClick={onCancel}
            style={{ marginLeft: 'auto' }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="merge-requirements-submit"
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? 'Merging…' : 'Merge'}
          </button>
        </footer>
      </div>
    </div>
  );
}
